import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** Header navigation, native modal dialogs, hero state, and live cart quantity. */
class MinoriaHeader extends Component {
  #controller;
  #resizeObserver;
  #frame = 0;
  #hero;
  #opener;
  #scrollLock;
  #ownsLock = false;
  #cartVersion = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #setup() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#findHero();
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('keydown', this.#keydown, { signal });
    this.addEventListener('focusout', this.#focusout, { signal });
    this.addEventListener('toggle', this.#toggle, { signal, capture: true });
    for (const dialog of this.querySelectorAll('dialog')) {
      dialog.addEventListener('close', this.#restore, { signal });
    }
    // Capture scroll events from both the desktop .page-wrapper and the mobile document.
    document.addEventListener('scroll', this.#schedule, { signal, capture: true, passive: true });
    window.addEventListener('resize', this.#schedule, { signal, passive: true });
    window.addEventListener('pageshow', this.#schedule, { signal });
    document.addEventListener('pointerdown', this.#outside, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#cartUpdate, { signal });
    document.addEventListener('shopify:section:load', this.#refreshHero, { signal });
    document.addEventListener('shopify:section:unload', this.#refreshHero, { signal });
    document.addEventListener('shopify:section:reorder', this.#refreshHero, { signal });
    this.#resizeObserver = new ResizeObserver(this.#schedule);
    for (const target of [this, this.querySelector('.minoria-nav-list--desktop'), this.querySelector('.minoria-logo-link'), this.#hero, this.closest('.page-wrapper')]) {
      if (target) this.#resizeObserver.observe(target);
    }
    document.fonts.ready.then(() => { if (!signal.aborted) this.#schedule(); });
    window.addEventListener('minoria:favorites:updated', this.#updateWishlistBadge, { signal });
    this.#updateWishlistBadge();
    this.#measure();
  }

  #cleanup() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#cartVersion++;
    this.querySelector('dialog[open]')?.close();
    this.#restore();
  }

  #findHero() {
    this.#hero = document.querySelector('#MainContent > .shopify-section:first-child .hero');
  }

  #refreshHero = () => {
    if (this.#hero) this.#resizeObserver?.unobserve(this.#hero);
    this.#findHero();
    if (this.#hero) this.#resizeObserver?.observe(this.#hero);
    this.#schedule();
  };

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#measure();
    });
  };

  #measure() {
    const container = this.querySelector('.minoria-header-container');
    const nav = this.querySelector('.minoria-nav-list--desktop');
    const logo = this.querySelector('.minoria-logo-link');
    if (!container || !nav || !logo) return;
    const styles = getComputedStyle(container);
    const padding = parseFloat(styles.paddingInlineStart) + parseFloat(styles.paddingInlineEnd);
    const actionCount = this.querySelector('.minoria-header-actions')?.children.length ?? 1;
    const sideWidth = Math.max(180, logo.scrollWidth, actionCount * 46 - 2);
    const required = nav.scrollWidth + sideWidth * 2 + 48 + padding;
    const desktop = window.innerWidth >= 1000 && container.clientWidth >= required;
    const nextLayout = desktop ? 'desktop' : 'mobile';
    if (this.dataset.layout !== nextLayout) {
      const active = document.activeElement;
      this.dataset.layout = nextLayout;
      this.#closeMenus();
      if (desktop && this.querySelector('[data-dialog="menu"][open]')) {
        this.querySelector('[data-dialog="menu"]').close();
      } else if (!desktop && active instanceof HTMLElement && this.querySelector('.minoria-desktop-nav')?.contains(active)) {
        this.querySelector('[data-open-dialog="menu"]')?.focus();
      }
    }
    document.body.style.setProperty('--minoria-header-height', `${this.offsetHeight}px`);
    const solid = this.dataset.transparent !== 'true' || !this.#hero || this.#hero.getBoundingClientRect().bottom <= this.getBoundingClientRect().bottom;
    this.classList.toggle('is-solid', solid);
  }

  #closeMenus(except) {
    for (const details of this.querySelectorAll('.minoria-desktop-nav details[open]')) {
      if (details !== except && !details.contains(except)) details.open = false;
    }
  }

  #toggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));
    if (details.open && details.matches('.minoria-desktop-nav [data-nav-disclosure]')) this.#closeMenus(details);
    if (!details.open) {
      for (const child of details.querySelectorAll('details[open]')) child.open = false;
    }
  };

  #outside = (event) => {
    if (!(event.target instanceof Node) || !this.querySelector('.minoria-desktop-nav')?.contains(event.target)) this.#closeMenus();
  };

  #focusout = (event) => {
    const nav = this.querySelector('.minoria-desktop-nav');
    if (nav?.contains(event.target) && !nav.contains(event.relatedTarget)) this.#closeMenus();
  };

  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    const cartTrigger = event.target.closest('[data-cart-link], [aria-controls="cart-drawer"]');
    if (cartTrigger) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.#closeMenus();
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else if (cartDrawer) {
        cartDrawer.setAttribute('open', '');
        cartDrawer.querySelector('dialog')?.showModal?.();
      } else {
        window.location.href = '/cart';
      }
      return;
    }
    const favTrigger = event.target.closest('[data-wishlist-open]');
    if (favTrigger) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.#closeMenus();
      this.querySelector('dialog[open]')?.close();
      window.MinoriaWishlist?.open?.();
      return;
    }
    const opener = event.target.closest('[data-open-dialog]');
    if (opener instanceof HTMLElement) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const dialog = this.querySelector(`[data-dialog="${opener.dataset.openDialog}"]`);
      if (!(dialog instanceof HTMLDialogElement)) return;
      event.preventDefault();
      this.#closeMenus();
      this.#opener = opener;
      this.#scrollLock = document.documentElement.getAttribute('scroll-lock');
      this.#ownsLock = true;
      document.documentElement.setAttribute('scroll-lock', '');
      opener.setAttribute('aria-expanded', 'true');
      dialog.showModal();
      return;
    }
    if (event.target.closest('[data-close-dialog]')) event.target.closest('dialog')?.close();
    if (event.target instanceof HTMLDialogElement) {
      const bounds = event.target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) event.target.close();
    }
  };

  #restore = () => {
    if (this.#ownsLock) {
      if (this.#scrollLock === null) document.documentElement.removeAttribute('scroll-lock');
      else document.documentElement.setAttribute('scroll-lock', this.#scrollLock);
      this.#ownsLock = false;
    }
    this.#opener?.setAttribute('aria-expanded', 'false');
    if (this.#opener?.isConnected && this.#opener.getClientRects().length) this.#opener.focus({ preventScroll: true });
    else if (this.#opener && this.isConnected) this.querySelector('.minoria-logo-link')?.focus({ preventScroll: true });
    this.#opener = null;
  };

  #keydown = (event) => {
    if (!(event.target instanceof Element)) return;
    const dialog = this.querySelector('dialog[open]');
    // Native dialog supplies modality, Escape, and focus containment. Explicit cycling
    // keeps Tab inside the document rather than allowing it to reach browser chrome.
    if (dialog && event.key === 'Tab') {
      const focusable = [...dialog.querySelectorAll('a[href], button, input, summary, [tabindex="0"]')]
        .filter((element) => element.getClientRects().length && !element.disabled);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    if (event.key === 'Escape' && !dialog) {
      const details = event.target.closest('details[open]');
      if (details) {
        event.preventDefault();
        details.open = false;
        details.querySelector('summary')?.focus();
      }
    }
    if (event.key === 'ArrowDown' && event.target.matches('.minoria-desktop-nav summary')) {
      event.preventDefault();
      const details = event.target.closest('details');
      details.open = true;
      details.querySelector('a')?.focus();
    }
  };

  #cartUpdate = async (event) => {
    const version = ++this.#cartVersion;
    try {
      const result = await event.promise;
      if (!this.isConnected || version !== this.#cartVersion) return;
      const count = result?.cart?.totalQuantity;
      if (typeof count !== 'number') return;
      const badge = this.querySelector('[data-cart-badge]');
      const label = this.querySelector('[data-cart-count]');
      if (badge) { badge.textContent = count > 99 ? '99+' : String(count); badge.hidden = count === 0; }
      if (label) label.textContent = String(count);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[minoria-header] Cart update failed:', error);
    }
  };

  #updateWishlistBadge = () => {
    try {
      const items = JSON.parse(localStorage.getItem('minoria_favorites') || '[]');
      const count = Array.isArray(items) ? items.length : 0;
      const badges = this.querySelectorAll('[data-wishlist-badge]');
      badges.forEach((b) => {
        b.textContent = count > 99 ? '99+' : String(count);
        b.hidden = count === 0;
      });
      const countTexts = document.querySelectorAll('[data-wishlist-count-text]');
      countTexts.forEach((t) => {
        t.textContent = String(count);
      });
    } catch (e) {}
  };
}

if (!customElements.get('minoria-header')) customElements.define('minoria-header', MinoriaHeader);
